import { maximalRectanglesOptimize } from './maximalRectangles.js';
import { guillotineOptimize, guillotineMINASOptimize, guillotineSLASOptimize } from './guillotine.js';
import { skylineWMOptimize } from './skylineWM.js';
import { scoreToCost } from './common.js';

/**
 * Simulated Annealing meta-heuristic optimizer.
 *
 * Optimizes both piece ordering AND which packer variant to use.
 * Accepts an optional warm-start result (from Best Result's deterministic pass)
 * so it is never worse than the best deterministic outcome.
 */

const innerPackers = [
  maximalRectanglesOptimize,
  guillotineOptimize,
  guillotineMINASOptimize,
  guillotineSLASOptimize,
  skylineWMOptimize,
];

/**
 * Generate a neighbor solution by mutating the ordering or packer index.
 */
function neighbor(ordering, packerIdx, rng) {
  const newOrdering = [...ordering];
  const r = rng();

  if (r < 0.70) {
    // Swap two random pieces
    const i = Math.floor(rng() * newOrdering.length);
    let j = Math.floor(rng() * newOrdering.length);
    if (j === i) j = (j + 1) % newOrdering.length;
    [newOrdering[i], newOrdering[j]] = [newOrdering[j], newOrdering[i]];
    return { ordering: newOrdering, packerIdx };
  } else if (r < 0.85) {
    // Reverse a subsequence of 2-5 pieces
    const len = 2 + Math.floor(rng() * 4); // 2 to 5
    const start = Math.floor(rng() * Math.max(1, newOrdering.length - len + 1));
    const end = Math.min(start + len, newOrdering.length);
    const sub = newOrdering.slice(start, end).reverse();
    for (let k = 0; k < sub.length; k++) {
      newOrdering[start + k] = sub[k];
    }
    return { ordering: newOrdering, packerIdx };
  } else {
    // Switch to a different inner packer
    let newIdx = Math.floor(rng() * innerPackers.length);
    if (newIdx === packerIdx) newIdx = (newIdx + 1) % innerPackers.length;
    return { ordering: newOrdering, packerIdx: newIdx };
  }
}

/**
 * Simple fast PRNG (mulberry32)
 */
function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Simulated Annealing optimizer.
 *
 * @param {Array} allPieces - All pieces to pack
 * @param {number} sheetWidth
 * @param {number} sheetHeight
 * @param {number} cutWidth - Kerf / blade width
 * @param {object} [warmStartResult] - Optional result from deterministic pass
 * @returns {object} Best result found: { layouts, unplacedPieces, unplacedCount, algorithmUsed? }
 */
export const simulatedAnnealingOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, warmStartResult) => {
  // Adaptive time budget: scale with piece count, cap at 5s
  const n = allPieces.length;
  const TIME_LIMIT_MS = Math.min(5000, Math.max(1000, n * 30));
  const STALE_LIMIT = 200; // stop if no improvement for this many iterations
  const startTime = performance.now();
  const sheetArea = sheetWidth * sheetHeight;
  const rng = mulberry32(Date.now() ^ 0xdeadbeef);

  let currentOrdering = [...allPieces];
  let currentPackerIdx = 0;
  let currentResult;
  let currentCost;

  if (warmStartResult && warmStartResult.layouts) {
    currentResult = warmStartResult;
    currentCost = scoreToCost(currentResult, sheetArea);
  } else {
    currentResult = innerPackers[0](allPieces, sheetWidth, sheetHeight, cutWidth);
    currentCost = scoreToCost(currentResult, sheetArea);
  }

  let bestResult = currentResult;
  let bestCost = currentCost;

  let temperature = 50.0;
  const coolingRate = 0.995;
  const minTemperature = 0.01;

  let iterations = 0;
  let staleSince = 0;

  while (performance.now() - startTime < TIME_LIMIT_MS && temperature > minTemperature) {
    const { ordering: newOrdering, packerIdx: newPackerIdx } = neighbor(currentOrdering, currentPackerIdx, rng);

    const newResult = innerPackers[newPackerIdx](newOrdering, sheetWidth, sheetHeight, cutWidth);
    const newCost = scoreToCost(newResult, sheetArea);

    const delta = newCost - currentCost;

    if (delta < 0 || Math.exp(-delta / temperature) > rng()) {
      currentOrdering = newOrdering;
      currentPackerIdx = newPackerIdx;
      currentResult = newResult;
      currentCost = newCost;

      if (newCost < bestCost) {
        bestResult = newResult;
        bestCost = newCost;
        staleSince = 0;
      } else {
        staleSince++;
      }
    } else {
      staleSince++;
    }

    // Convergence detection
    if (staleSince >= STALE_LIMIT) break;

    temperature *= coolingRate;
    iterations++;
  }

  return bestResult;
};
